import React, { useState } from 'react';
import { X, Airplane } from '@phosphor-icons/react';
import { TRIP_STATUS } from './TripCard';

const CURRENCIES = ['INR', 'USD', 'EUR', 'GBP', 'AED', 'SGD'];

export default function TripEditModal({ api, trip, onClose, onSaved }) {
  const [form, setForm] = useState({
    title: trip?.title || '',
    destination: trip?.destination || '',
    start_date: trip?.start_date || '',
    end_date: trip?.end_date || '',
    party_size: trip?.party_size ?? 2,
    budget_amount: trip?.budget_amount ?? 0,
    currency: trip?.currency || 'INR',
    notes: trip?.notes || '',
    status: trip?.status || 'planned',
    travellers: Array.isArray(trip?.travellers) ? trip.travellers.join(', ') : '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSave = async () => {
    setError('');
    if (!form.title.trim()) { setError('Title is required'); return; }
    if (!form.destination.trim()) { setError('Destination is required'); return; }
    if (form.start_date && form.end_date && form.end_date < form.start_date) {
      setError('End date must be on/after start date');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: form.title,
        destination: form.destination,
        start_date: form.start_date,
        end_date: form.end_date,
        party_size: Number(form.party_size) || 1,
        budget_amount: Number(form.budget_amount) || 0,
        currency: form.currency,
        notes: form.notes,
        status: form.status,
        travellers: (form.travellers || '')
          .split(',')
          .map(s => s.trim())
          .filter(Boolean)
          .slice(0, 20),
      };
      const r = trip
        ? await api.put(`/trips/${trip.trip_id}`, payload)
        : await api.post('/trips', payload);
      onSaved && onSaved(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Save failed');
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center sm:p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} data-testid="trip-edit-modal"
        className="bg-neutral-900 border border-white/10 rounded-t-2xl sm:rounded-2xl w-full sm:max-w-lg max-h-[92vh] overflow-y-auto">
        <div className="sticky top-0 z-10 px-5 py-3 border-b border-white/10 bg-neutral-900 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Airplane size={18} weight="fill" className="text-[#FF6B35]" />
            <h3 className="text-sm font-bold text-white">{trip ? `Edit · ${trip.title}` : 'New Trip'}</h3>
          </div>
          <button onClick={onClose} className="text-neutral-400 hover:text-white"><X size={18} /></button>
        </div>

        <div className="p-5 space-y-3">
          {error && <div className="text-xs text-red-300 px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/30">{error}</div>}

          <Field label="Trip title">
            <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              data-testid="trip-title" className="trip-input" placeholder="Goa long weekend, Spiti Valley expedition…" />
          </Field>

          <Field label="Destination">
            <input value={form.destination} onChange={e => setForm(f => ({ ...f, destination: e.target.value }))}
              data-testid="trip-destination" className="trip-input" placeholder="Goa, India" />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Start date">
              <input type="date" value={form.start_date} onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))}
                data-testid="trip-start-date" className="trip-input" />
            </Field>
            <Field label="End date">
              <input type="date" value={form.end_date} onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))}
                data-testid="trip-end-date" className="trip-input" />
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Party size">
              <input type="number" min="1" max="50" value={form.party_size}
                onChange={e => setForm(f => ({ ...f, party_size: e.target.value }))}
                data-testid="trip-party-size" className="trip-input" />
            </Field>
            <Field label="Currency">
              <select value={form.currency} onChange={e => setForm(f => ({ ...f, currency: e.target.value }))}
                data-testid="trip-currency" className="trip-input">
                {CURRENCIES.map(c => <option key={c} value={c} style={{ background: '#0a0a0a' }}>{c}</option>)}
              </select>
            </Field>
          </div>

          <Field label="Total budget">
            <input type="number" min="0" step="500" value={form.budget_amount}
              onChange={e => setForm(f => ({ ...f, budget_amount: e.target.value }))}
              data-testid="trip-budget" className="trip-input" />
          </Field>

          <Field label="Travellers (for expense splitting)">
            <input value={form.travellers}
              onChange={e => setForm(f => ({ ...f, travellers: e.target.value }))}
              data-testid="trip-travellers" className="trip-input"
              placeholder="Me, Priya, Aarav (comma separated)" />
            <p className="text-[10px] text-neutral-500 mt-1">Used by the Budget tab to split costs between travellers.</p>
          </Field>

          {trip && (
            <Field label="Status">
              <div className="flex flex-wrap gap-1.5">
                {TRIP_STATUS.map(s => (
                  <button key={s.id} type="button" onClick={() => setForm(f => ({ ...f, status: s.id }))}
                    data-testid={`trip-status-${s.id}`}
                    className={`px-2.5 py-1 rounded-full text-[10px] font-bold border ${form.status === s.id ? 'text-white' : 'text-neutral-400'}`}
                    style={form.status === s.id
                      ? { background: s.tint, borderColor: s.color, color: s.color }
                      : { background: 'rgba(255,255,255,0.05)', borderColor: 'rgba(255,255,255,0.10)' }}>
                    {s.label}
                  </button>
                ))}
              </div>
            </Field>
          )}

          <Field label="Notes">
            <textarea rows="3" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              data-testid="trip-notes" className="trip-input" placeholder="Anything special — anniversary, kids' first beach trip, food restrictions…" />
          </Field>
        </div>

        <div className="sticky bottom-0 px-5 py-3 border-t border-white/10 bg-neutral-900 flex justify-end gap-2">
          <button onClick={onClose} disabled={saving} className="px-3 py-2 rounded-lg bg-white/5 text-neutral-300 text-xs font-semibold hover:bg-white/10">Cancel</button>
          <button onClick={handleSave} disabled={saving} data-testid="trip-save-btn"
            className="px-4 py-2 rounded-lg bg-[#FF6B35] text-white text-xs font-bold hover:bg-[#E85D2A] disabled:opacity-50">
            {saving ? 'Saving…' : (trip ? 'Save changes' : 'Create trip')}
          </button>
        </div>

        <style>{`.trip-input{width:100%;padding:8px 12px;border-radius:10px;background:rgba(255,255,255,0.05);border:1px solid rgba(255,255,255,0.10);font-size:13px;color:#fff;outline:none}.trip-input:focus{border-color:#FF6B35}`}</style>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-wider font-bold text-neutral-500 block mb-1">{label}</label>
      {children}
    </div>
  );
}
